import { useState } from "react";

const PLANS = [
  {
    id: 'career-destination',
    name: '🎯 Career Destination Session',
    tag: 'ONCE-OFF · START HERE',
    tagBg: '#1a3a6c',
    price: { once: 499 },
    billing: ['once'],
    billingLabels: { once: 'R499 once-off' },
    features: [
      '90-min Career Discovery Session',
      'Career Destination Report',
      'Signed Career Destination Declaration',
      'Subject & grade requirements mapped',
      'University options documented',
      'Upgrade to Navigator anytime',
    ],
    note: '⚠ Best if budget is the only constraint',
  },
  {
    id: 'navigator',
    name: '🧭 HSCN Navigator',
    tag: 'MOST POPULAR',
    tagBg: '#C9921C',
    strikethrough: 'R7,000',
    price: { annual: 4000, termly: 1100, monthly: 380 },
    billing: ['annual', 'termly', 'monthly'],
    billingLabels: {
      annual: 'R4,000/year — BEST VALUE',
      termly: 'R1,100/term × 4 = R4,400',
      monthly: 'R380/month × 12 = R4,560',
    },
    features: [
      'Complete 4-Phase HSCN navigation system',
      'Career Destination Report & Declaration',
      'Pathway Activation Pack (all documents)',
      '3× term Navigation Status Reports',
      'Tutor coordination — max 2 subjects',
      'Re-navigation when goals change',
    ],
    popular: true,
  },
  {
    id: 'navigator-plus',
    name: '🚀 HSCN Navigator Plus',
    tag: 'PREMIUM',
    tagBg: '#C9921C',
    strikethrough: 'R12,000',
    price: { annual: 8500, termly: 2300, monthly: 800 },
    billing: ['annual', 'termly', 'monthly'],
    billingLabels: {
      annual: 'R8,500/year — SAVE R3,500',
      termly: 'R2,300/term × 4 = R9,200',
      monthly: 'R800/month × 12 = R9,600',
    },
    features: [
      'Everything in Navigator, PLUS:',
      'Monthly progress calls & reviews',
      'Tutor coordination — all subjects',
      '2× professional mentorship sessions/year',
      'Full university application support',
      'Scholarship & financial aid assistance',
      'Priority WhatsApp (4-hr response)',
      '🎁 Business Builder Blueprint™ (R4,000 value)',
    ],
    bonus: true,
  },
];

export default function PaystackCheckout({ parentEmail, parentName, studentName, phone }) {
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailInput, setEmailInput] = useState(parentEmail || '');

  const getAmount = (plan, billing) => plan.price[billing] || plan.price.once;

  const handlePay = async () => {
    if (!selected) return;
    if (!emailInput.trim() || !/\S+@\S+\.\S+/.test(emailInput)) {
      setError('Please enter a valid email address so we can send your receipt.');
      return;
    }
    setLoading(true);
    setError('');

    const plan = PLANS.find(p => p.id === selected.planId);
    const amount = getAmount(plan, selected.billing);

    try {
      const res = await fetch('/api/paystack/initiate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: emailInput.trim(),
          amount,
          plan: selected.planId,
          billing: selected.billing,
          studentName,
          parentName,
          phone,
        }),
      });

      const data = await res.json();

      if (data.authorization_url) {
        window.location.href = data.authorization_url;
      } else {
        setError(data.error || 'Payment failed. Please try again.');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const selectedPlan = selected ? PLANS.find(p => p.id === selected.planId) : null;
  const firstName = parentName ? parentName.split(' ')[0] : '';

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '32px 16px', fontFamily: 'sans-serif' }}>

      {/* Personalised Header */}
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>🧭</div>
        {firstName && studentName ? (
          <>
            <h2 style={{ color: '#1a3a6c', fontSize: 22, margin: '0 0 8px' }}>
              Hi {firstName} — {studentName}'s Career Destination Roadmap is one step away.
            </h2>
            <p style={{ color: '#666', fontSize: 14, margin: 0 }}>
              Choose the level of support that works for your family. Secure payment via Paystack.
            </p>
          </>
        ) : (
          <>
            <h2 style={{ color: '#1a3a6c', fontSize: 22, margin: '0 0 8px' }}>
              Choose Your Career Destination Plan
            </h2>
            <p style={{ color: '#666', fontSize: 14, margin: 0 }}>
              Three levels of support. One goal: your child's success.
            </p>
          </>
        )}
      </div>

      {/* Pricing Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20, marginBottom: 32 }}>
        {PLANS.map(plan => (
          <div key={plan.id} style={{
            border: `2px solid ${selected?.planId === plan.id ? '#1a3a6c' : '#e5e7eb'}`,
            borderRadius: 12,
            overflow: 'hidden',
            background: '#fff',
            boxShadow: plan.popular
              ? '0 4px 20px rgba(26,58,108,0.15)'
              : '0 2px 8px rgba(0,0,0,0.06)',
            transition: 'border-color 0.2s',
          }}>
            <div style={{
              background: plan.bonus ? '#C9921C' : plan.popular ? '#1a3a6c' : '#f3f4f6',
              padding: '16px 20px',
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: plan.bonus || plan.popular ? '#fff' : '#C9921C', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                {plan.tag}
              </div>
              <div style={{ fontSize: 16, fontWeight: 800, color: plan.bonus || plan.popular ? '#fff' : '#1a3a6c' }}>
                {plan.name}
              </div>
              {plan.strikethrough && (
                <div style={{ fontSize: 11, color: plan.bonus || plan.popular ? 'rgba(255,255,255,0.7)' : '#999', marginTop: 4 }}>
                  Regular: <s>{plan.strikethrough}</s>
                </div>
              )}
            </div>

            <div style={{ padding: '16px 20px' }}>
              {plan.billing.map(b => (
                <label key={b} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', marginBottom: 8,
                  borderRadius: 8, cursor: 'pointer',
                  background: selected?.planId === plan.id && selected?.billing === b ? '#EFF6FF' : '#f9fafb',
                  border: `1px solid ${selected?.planId === plan.id && selected?.billing === b ? '#1a3a6c' : '#e5e7eb'}`,
                  transition: 'all 0.15s',
                }}>
                  <input type="radio" name={`billing-${plan.id}`} value={b}
                    checked={selected?.planId === plan.id && selected?.billing === b}
                    onChange={() => setSelected({ planId: plan.id, billing: b })}
                    style={{ accentColor: '#1a3a6c', width: 16, height: 16 }}/>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#1a3a6c' }}>
                    {plan.billingLabels[b]}
                  </span>
                </label>
              ))}

              <ul style={{ listStyle: 'none', padding: 0, margin: '16px 0 0', fontSize: 12, color: '#444', lineHeight: 2 }}>
                {plan.features.map((f, i) => (
                  <li key={i} style={{ display: 'flex', gap: 6 }}>
                    <span style={{ color: '#1a3a6c', fontWeight: 700 }}>✓</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              {plan.bonus && (
                <div style={{ background: '#FFF8EC', border: '1px solid #C9921C', borderRadius: 8, padding: '10px 12px', marginTop: 14, fontSize: 12, color: '#7a5c00', lineHeight: 1.7 }}>
                  <strong>🎁 EXCLUSIVE BONUS — R4,000 VALUE, YOURS FREE</strong><br /><br />
                  <strong>Business Builder Blueprint™</strong><br />
                  10 structured missions that teach your child how to build income from scratch.<br /><br />
                  <strong>What your child gets:</strong><br />
                  ✓ How to identify a profitable business idea<br />
                  ✓ How to get their first paying customer<br />
                  ✓ How to price, sell and deliver a service<br />
                  ✓ How to build income while still in school<br />
                  ✓ The entrepreneurship mindset no school teaches<br /><br />
                  <em>Sold separately at R4,000 — included at no extra cost in Navigator Plus only.</em>
                </div>
              )}

              {plan.note && (
                <p style={{ fontSize: 11, color: '#e67e22', marginTop: 12, marginBottom: 0 }}>{plan.note}</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Email fallback — only shown if email not passed via URL */}
      {!parentEmail && (
        <div style={{ maxWidth: 400, margin: '0 auto 20px' }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
            Your email address (for receipt & report delivery)
          </label>
          <input
            type="email"
            value={emailInput}
            onChange={e => setEmailInput(e.target.value)}
            placeholder="your@email.com"
            style={{ width: '100%', padding: '10px 12px', borderRadius: 8, fontSize: 14,
              border: '1.5px solid #e5e7eb', outline: 'none', boxSizing: 'border-box' }}
          />
        </div>
      )}

      {error && (
        <p style={{ color: '#c0392b', textAlign: 'center', marginBottom: 16, fontSize: 13 }}>{error}</p>
      )}

      <div style={{ textAlign: 'center' }}>
        <button onClick={handlePay} disabled={!selected || loading} style={{
          background: selected && !loading ? '#1a3a6c' : '#ccc',
          color: '#fff', border: 'none', borderRadius: 8, padding: '16px 48px',
          fontSize: 16, fontWeight: 700, cursor: selected && !loading ? 'pointer' : 'not-allowed',
          transition: 'background 0.2s', minWidth: 280,
        }}>
          {loading ? 'Redirecting to payment...'
            : selected ? `Pay ${selectedPlan?.billingLabels[selected.billing]} →`
            : 'Select a plan to continue →'}
        </button>
        <p style={{ fontSize: 11, color: '#999', marginTop: 10 }}>
          🔒 Secure payment via Paystack · ZAR · Your card details never touch this site
        </p>
      </div>
    </div>
  );
}
